import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react'
import { useLocation, useNavigate, useRouteError } from 'react-router'
import { CloudUpload, FolderCheck, House, MapPinOff, RotateCw, Settings, TriangleAlert } from 'lucide-react'
import { Button } from '@/app/components/ui/Button'
import { ErrorPanel } from '@/app/components/ErrorPanel'
import { describeError, errorReport, type DescribedError } from '@/app/lib/errors'

/**
 * Every error page in the console, and where each one sits.
 *
 * React Router's default boundary showed a raw stack trace and "Hey developer"
 * — in front of a guest, with no way back but finding the address bar. There
 * are five places an error can surface, and each needs a different screen:
 *
 *  - a SCREEN crashed (the flow, Settings, the designer) → `RouteError`,
 *    rendered inside the shell, so the banners and the upload queue live on;
 *  - the SHELL crashed → `ShellError`, the whole viewport;
 *  - an address matches nothing → `NotFound`, which is not a bug and says so;
 *  - the GUEST screen crashed → `GuestError`, which a guest may be looking at,
 *    so it shows no error at all and quietly reloads itself;
 *  - and anything escaping the router entirely → `AppErrorBoundary`.
 */

/** A screen inside the console failed. The shell around it is still running. */
export function RouteError() {
  const error = useRouteError()
  const described = describeError(error)
  useLogOnce(error)

  if (described.kind === 'notFound') return <NotFoundPanel layout="outlet" />
  return <CrashPanel described={described} scope="screen" />
}

/**
 * The shell itself failed — AppShell, its banners, its capability probe. Its
 * upload queue went with it, which the notes say, and a reload restarts it.
 */
export function ShellError() {
  const error = useRouteError()
  const described = describeError(error)
  useLogOnce(error)

  if (described.kind === 'notFound') return <NotFoundPanel layout="screen" />
  return <CrashPanel described={described} scope="shell" />
}

/** The `*` route: an address that exists nowhere in the console. */
export function NotFound() {
  return <NotFoundPanel layout="outlet" />
}

function NotFoundPanel({ layout }: { layout: 'outlet' | 'screen' }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return (
    <ErrorPanel
      layout={layout}
      tone="brand"
      icon={<MapPinOff size={28} />}
      eyebrow="404"
      title="Halaman tidak ditemukan"
      actions={
        <>
          <Button size="lg" onClick={() => navigate('/')}>
            <House size={18} />
            Kembali ke konsol
          </Button>
          <Button size="lg" variant="secondary" onClick={() => navigate('/settings')}>
            <Settings size={18} />
            Pengaturan
          </Button>
        </>
      }
    >
      Alamat{' '}
      <code className="rounded-[6px] bg-raised px-1.5 py-0.5 font-mono text-[14px] text-ink">
        {pathname}
      </code>{' '}
      tidak ada di konsol ini. Mungkin tautannya salah ketik atau sudah tidak dipakai.
    </ErrorPanel>
  )
}

/**
 * A crash, anywhere operator-facing.
 *
 * Deliberately free of router hooks: `AppErrorBoundary` renders it from
 * outside the router, and both recovery actions are full page loads rather
 * than client-side navigation. Navigating inside a router whose screen just
 * threw tends to re-render into the same broken state and throw again; a load
 * starts every store over.
 */
function CrashPanel({
  described,
  scope,
}: {
  described: DescribedError
  /** What failed: a screen inside the shell, the shell, or the whole app. */
  scope: 'screen' | 'shell' | 'app'
}) {
  const [at] = useState(() => new Date())
  const report = errorReport(described, {
    at,
    href: window.location.href,
    userAgent: navigator.userAgent,
  })
  const atRoot = window.location.pathname === '/'

  return (
    <ErrorPanel
      layout={scope === 'screen' ? 'outlet' : 'screen'}
      tone="danger"
      icon={<TriangleAlert size={28} />}
      eyebrow="Terjadi kesalahan"
      title={scope === 'screen' ? 'Layar ini berhenti bekerja' : 'Konsol berhenti bekerja'}
      notes={[
        {
          icon: <CloudUpload size={18} />,
          text:
            // Only a screen crash leaves AppShell — and so the queue — running.
            scope === 'screen'
              ? 'Unggahan sesi tetap berjalan — bagian lain konsol masih bekerja.'
              : 'Sesi yang sudah masuk antrean tersimpan di PC ini dan diunggah lagi setelah dimuat ulang.',
        },
        {
          icon: <FolderCheck size={18} />,
          text: 'Izin folder tetap berlaku. Setelah dimuat ulang, tekan “Mulai pantau” lagi.',
        },
      ]}
      actions={
        <>
          <Button size="lg" onClick={() => window.location.reload()}>
            <RotateCw size={18} />
            Muat ulang konsol
          </Button>
          {/* On "/" this would be the same as reloading — two buttons that do
              one thing make the operator stop and wonder which is right. */}
          {!atRoot && (
            <Button size="lg" variant="secondary" onClick={() => window.location.assign('/')}>
              <House size={18} />
              Kembali ke awal
            </Button>
          )}
        </>
      }
      report={report}
      at={at}
    >
      Terjadi kesalahan yang tidak terduga. Muat ulang untuk melanjutkan. Jika terjadi lagi,
      tekan <span className="text-ink">Salin detail</span> di bawah dan kirimkan ke tim.
    </ErrorPanel>
  )
}

/** Delays before the guest screen reloads itself, escalating while it keeps failing. */
const GUEST_RELOAD_DELAYS_S = [10, 30, 60, 120]
const GUEST_RELOAD_KEY = 'citaria-guest-reloads'
/** A failure more than this long after the last reload starts the ladder over. */
const GUEST_RELOAD_WINDOW_MS = 5 * 60_000

/**
 * The guest screen failed.
 *
 * A guest is likely the one looking at this, on a second monitor nobody at the
 * booth is watching. So it shows no error, no stack and no jargon — just the
 * same calm branded screen as between guests — and it repairs itself by
 * reloading. The reload delay escalates while it keeps failing, so a screen
 * that crashes on every load settles into a slow retry rather than flashing
 * in front of a queue of guests every ten seconds.
 */
export function GuestError() {
  const error = useRouteError()
  useLogOnce(error)

  const [delay] = useState(nextGuestReloadDelay)

  useEffect(() => {
    const timer = setTimeout(() => {
      recordGuestReload()
      window.location.reload()
    }, delay * 1000)
    return () => clearTimeout(timer)
  }, [delay])

  return (
    <div
      role="alert"
      className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-brand-deep px-8 text-center"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(120% 90% at 50% 0%, rgba(255,255,255,.16), transparent 60%)',
        }}
      />
      <div className="relative flex flex-col items-center">
        <h1 className="text-[clamp(30px,5vw,72px)] font-bold leading-tight text-white">
          Citaria Photobooth
        </h1>
        <p className="mt-4 text-[clamp(16px,2vw,28px)] text-white/75">
          Layar sedang disiapkan ulang — sebentar lagi kembali
        </p>
        <div className="mt-[6vh] flex gap-3" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="citaria-live h-3 w-3 rounded-full bg-white/70"
              style={{ animationDelay: `${i * 0.25}s` }}
            />
          ))}
        </div>
      </div>

      {/* For an operator who happens to be standing at this monitor. Faint on
          purpose: to a guest it should read as nothing. */}
      <button
        type="button"
        onClick={() => {
          recordGuestReload()
          window.location.reload()
        }}
        className="absolute bottom-6 min-h-[44px] px-4 text-[13px] text-white/35 underline underline-offset-4"
      >
        Muat ulang sekarang
      </button>
    </div>
  )
}

function readGuestReloads(): { count: number; at: number } | null {
  try {
    const raw = sessionStorage.getItem(GUEST_RELOAD_KEY)
    return raw ? (JSON.parse(raw) as { count: number; at: number }) : null
  } catch {
    return null
  }
}

function nextGuestReloadDelay(): number {
  const previous = readGuestReloads()
  const recent = previous !== null && Date.now() - previous.at < GUEST_RELOAD_WINDOW_MS
  const count = recent ? previous.count : 0
  return GUEST_RELOAD_DELAYS_S[Math.min(count, GUEST_RELOAD_DELAYS_S.length - 1)]!
}

function recordGuestReload(): void {
  const previous = readGuestReloads()
  const recent = previous !== null && Date.now() - previous.at < GUEST_RELOAD_WINDOW_MS
  try {
    sessionStorage.setItem(
      GUEST_RELOAD_KEY,
      JSON.stringify({ count: (recent ? previous.count : 0) + 1, at: Date.now() }),
    )
  } catch {
    // Storage blocked: the reload still happens, just without the backoff.
  }
}

/**
 * The last resort, around the whole router in main.tsx.
 *
 * Route errors never reach here — the screens above catch them. This is for
 * what escapes the router itself, including an error screen that throws, which
 * would otherwise unmount everything and leave a blank page with no way back.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: unknown }
> {
  // A flag, not `error !== null`: JavaScript lets you `throw null`, and a
  // boundary keyed on the value would re-render the children that just threw.
  state: { hasError: boolean; error: unknown } = { hasError: false, error: null }

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, error }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Citaria: uncaught application error', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return <CrashPanel described={describeError(this.state.error)} scope="app" />
    }
    return this.props.children
  }
}

/**
 * Route errors are rendered, not thrown on, so without this they would never
 * reach the console — and devtools is where a developer looks first.
 */
function useLogOnce(error: unknown) {
  useEffect(() => {
    console.error('Citaria: route error', error)
  }, [error])
}
